// @react/components/ErrorAlert.tsx
// Auto-generated by corebuilder. DO NOT EDIT.

import { useState } from 'react';
import { Alert } from './Alert';
import { useI18n } from '../hooks/useI18n';
import type { ApiError, RequestContext } from '@core/api/client';

export interface ErrorAlertProps {
  error: unknown;
  dismissible?: boolean;
  onDismiss?: () => void;
}

function getErrorInfo(error: unknown): { message: string; requestId?: string; requestContext?: RequestContext } {
  if (error && typeof error === 'object' && 'message' in error) {
    const e = error as ApiError;
    return {
      message: e.message,
      requestId: e.requestId,
      requestContext: e.requestContext,
    };
  }
  if (typeof error === 'string') {
    return { message: error };
  }
  return { message: '' };
}

function buildCopyText(info: { message: string; requestId?: string; requestContext?: RequestContext }): string {
  const lines: string[] = [];
  if (info.requestId) lines.push(`Request ID: ${info.requestId}`);
  lines.push(`Error: ${info.message}`);
  if (info.requestContext) {
    lines.push(`Method: ${info.requestContext.method} ${info.requestContext.endpoint}`);
    if (info.requestContext.body) {
      lines.push(`Payload: ${JSON.stringify(info.requestContext.body, null, 2)}`);
    }
  }
  return lines.join('\n');
}

export function ErrorAlert({ error, dismissible, onDismiss }: ErrorAlertProps) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const info = getErrorInfo(error);

  if (!info.message) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(buildCopyText(info));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Alert variant="error" dismissible={dismissible} onDismiss={onDismiss}>
      <div className="space-y-2">
        <p>{info.message}</p>
        {info.requestId && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{t('errors.requestId')}: <code className="font-mono">{info.requestId}</code></span>
          </div>
        )}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1 text-xs font-medium text-error-foreground/80 hover:text-error-foreground underline underline-offset-2 transition-colors"
          >
            {copied ? t('errors.copied') : t('errors.copyDetails')}
          </button>
          <span className="text-xs text-muted-foreground">{t('errors.copyForAdmin')}</span>
        </div>
      </div>
    </Alert>
  );
}
